import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FiShield,
  FiUsers,
  FiActivity,
  FiDollarSign,
  FiUserX,
  FiGift,
  FiXCircle,
  FiX,
  FiFileText,
  FiClock,
  FiBarChart2,
  FiCpu,
  FiHardDrive,
  FiZap,
  FiRefreshCw,
  FiAlertTriangle,
} from 'react-icons/fi'
import { toast } from 'react-toastify'
import { api } from '../../services/api'
import { Spinner } from '../../components/Spinner'
import {
  Container,
  Header,
  StatsGrid,
  StatCard,
  Section,
  SectionHeader,
  SearchInput,
  Table,
  Badge,
  ActionButton,
  EmptyState,
  LoadingState,
  Pagination,
  ClickableRow,
  ModalOverlay,
  Modal,
  ModalHeader,
  ModalBody,
  LogList,
  LogItem,
  PeriodButtonGroup,
} from './styles'

const METRICS_PERIODS = [
  { key: '24h', label: '24 horas', ms: 24 * 60 * 60 * 1000 },
  { key: '7d', label: '7 dias', ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '30d', label: '30 dias', ms: 30 * 24 * 60 * 60 * 1000 },
]

const formatMs = (ms) => {
  if (ms === null || ms === undefined) return '-'
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(2)} s`
}

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return '-'
  const mb = bytes / (1024 * 1024)
  if (mb < 1024) return `${mb.toFixed(1)} MB`
  return `${(mb / 1024).toFixed(2)} GB`
}

export function Admin() {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loadingCourtesy, setLoadingCourtesy] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [logs, setLogs] = useState([])
  const [logsPage, setLogsPage] = useState(1)
  const [logsTotalPages, setLogsTotalPages] = useState(1)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [latestLogs, setLatestLogs] = useState([])
  const [latestLogsLimit, setLatestLogsLimit] = useState(10)
  const [latestLogsError, setLatestLogsError] = useState(null)
  const [loadingLatestLogs, setLoadingLatestLogs] = useState(false)
  const [metricsPeriod, setMetricsPeriod] = useState('24h')
  const [metricsData, setMetricsData] = useState({ requests: [], process: [] })
  const [loadingMetrics, setLoadingMetrics] = useState(false)
  const [metricsError, setMetricsError] = useState(null)

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/stats')
      setStats(data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }, [])

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/users', {
        params: { page, search: searchTerm, limit: 10 },
      })
      setUsers(data.users || [])
      setTotalPages(data.totalPages || 1)
    } catch (error) {
      console.error('Error fetching users:', error)
      toast.error('Erro ao carregar usuários')
    }
  }, [page, searchTerm])

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchStats(), fetchUsers()])
      setLoading(false)
    }
    loadData()
  }, [fetchStats, fetchUsers])

  const fetchLatestLogs = useCallback(async () => {
    setLoadingLatestLogs(true)
    setLatestLogsError(null)

    try {
      const { data } = await api.get('/admin/logs', {
        params: { limit: latestLogsLimit },
      })

      if (Array.isArray(data)) {
        setLatestLogs(data)
      } else {
        setLatestLogs(data.logs || [])
      }
    } catch (error) {
      console.error('Error fetching latest logs:', error)

      const status = error.response?.status
      if (status === 401) {
        navigate('/login', { replace: true })
        return
      }

      if (status === 403) {
        setLatestLogsError('forbidden')
      } else if (status === 500) {
        setLatestLogsError('server')
      } else {
        setLatestLogsError('generic')
      }

      setLatestLogs([])
    } finally {
      setLoadingLatestLogs(false)
    }
  }, [latestLogsLimit, navigate])

  useEffect(() => {
    fetchLatestLogs()
  }, [fetchLatestLogs])

  const fetchMetrics = useCallback(async () => {
    setLoadingMetrics(true)
    setMetricsError(null)

    try {
      const period = METRICS_PERIODS.find((p) => p.key === metricsPeriod)
      const since = new Date(Date.now() - period.ms).toISOString()
      const { data } = await api.get('/admin/metrics', {
        params: { since, requestLimit: 500, processLimit: 200 },
      })
      setMetricsData({ requests: data.requests || [], process: data.process || [] })
    } catch (error) {
      console.error('Error fetching metrics:', error)

      const status = error.response?.status
      if (status === 401) {
        navigate('/login', { replace: true })
        return
      }

      if (status === 403) {
        setMetricsError('forbidden')
      } else if (status === 500) {
        setMetricsError('server')
      } else {
        setMetricsError('generic')
      }

      setMetricsData({ requests: [], process: [] })
    } finally {
      setLoadingMetrics(false)
    }
  }, [metricsPeriod, navigate])

  useEffect(() => {
    fetchMetrics()
  }, [fetchMetrics])

  const metricsSummary = useMemo(() => {
    const requests = metricsData.requests
    const totalRequests = requests.reduce((sum, r) => sum + (r.requestCount || 0), 0)
    const totalErrors = requests.reduce((sum, r) => sum + (r.errorCount || 0), 0)
    const totalDuration = requests.reduce((sum, r) => sum + (r.totalDurationMs || 0), 0)
    const avgDuration = totalRequests > 0 ? totalDuration / totalRequests : 0
    const maxDuration = requests.reduce((max, r) => Math.max(max, r.maxDurationMs || 0), 0)
    return { totalRequests, totalErrors, avgDuration, maxDuration }
  }, [metricsData.requests])

  const sortedMetricsRows = useMemo(
    () => [...metricsData.requests].sort((a, b) => (b.requestCount || 0) - (a.requestCount || 0)),
    [metricsData.requests]
  )

  const latestProcessSample = useMemo(() => {
    return metricsData.process.reduce((latest, sample) => {
      if (!latest) return sample
      return new Date(sample.sampledAt) > new Date(latest.sampledAt) ? sample : latest
    }, null)
  }, [metricsData.process])

  const getPlanBadge = (user) => {
    if (!user.subscriptionStatus || user.subscriptionStatus === 'free') {
      return <Badge>Free</Badge>
    }

    switch (user.subscriptionStatus) {
      case 'active':
        if (!user.isPaying) {
          return <Badge $variant="info">Cortesia</Badge>
        }
        if (user.subscriptionTier === 'yearly') {
          return <Badge $variant="success">Anual</Badge>
        }
        return <Badge $variant="success">Mensal</Badge>
      case 'canceled':
        return <Badge $variant="danger">Cancelado</Badge>
      case 'past_due':
        return <Badge $variant="warning">Pendente</Badge>
      default:
        return <Badge>Free</Badge>
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
    setPage(1)
  }

  const handleCourtesy = async (userId, grant) => {
    setLoadingCourtesy(userId)
    try {
      await api.patch(`/admin/users/${userId}/courtesy`, { grant })
      toast.success(grant ? 'Cortesia concedida!' : 'Cortesia removida!')
      await fetchUsers()
      await fetchStats()
    } catch (error) {
      console.error('Error toggling courtesy:', error)
      toast.error(error.response?.data?.message || 'Erro ao alterar cortesia')
    } finally {
      setLoadingCourtesy(null)
    }
  }

  const canGrantCourtesy = (user) => {
    return !user.subscriptionStatus || user.subscriptionStatus === 'free' || user.subscriptionStatus === 'canceled'
  }

  const canRevokeCourtesy = (user) => {
    return user.subscriptionStatus === 'active' && !user.isPaying
  }

  const fetchLogs = async (userId, pageNum = 1) => {
    setLoadingLogs(true)
    try {
      const { data } = await api.get(`/admin/users/${userId}/logs`, {
        params: { page: pageNum, limit: 20 },
      })
      setLogs(data.logs || [])
      setLogsTotalPages(data.totalPages || 1)
      setLogsPage(pageNum)
    } catch (error) {
      console.error('Error fetching logs:', error)
      toast.error('Erro ao carregar histórico')
    } finally {
      setLoadingLogs(false)
    }
  }

  const handleUserClick = (user, e) => {
    if (e.target.closest('button')) return
    setSelectedUser(user)
    setLogsPage(1)
    fetchLogs(user.id, 1)
  }

  const closeModal = () => {
    setSelectedUser(null)
    setLogs([])
  }

  const handleLoadMoreLatestLogs = () => {
    setLatestLogsLimit((currentLimit) => Math.min(100, currentLimit + 10))
  }

  const formatLocalDateTime = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Container>
      <Header>
        <h1>
          <FiShield />
          Painel do Admin
        </h1>
        <p>Gerencie usuários e visualize estatísticas da plataforma</p>
      </Header>

      <StatsGrid>
        <StatCard $color="#6464ff">
          <div className="icon">
            <FiUsers />
          </div>
          <div className="info">
            <h3>{stats?.totalUsers ?? '-'}</h3>
            <p>Total de Usuários</p>
          </div>
        </StatCard>

        <StatCard $color="#10b981">
          <div className="icon">
            <FiDollarSign />
          </div>
          <div className="info">
            <h3>{stats?.activeSubscriptions ?? '-'}</h3>
            <p>Assinaturas Ativas</p>
          </div>
        </StatCard>

        <StatCard $color="#f59e0b">
          <div className="icon">
            <FiActivity />
          </div>
          <div className="info">
            <h3>{stats?.calculationsToday ?? '-'}</h3>
            <p>Cálculos Hoje</p>
          </div>
        </StatCard>

        <StatCard $color="#8b5cf6">
          <div className="icon">
            <FiActivity />
          </div>
          <div className="info">
            <h3>{stats?.calculationsTotal ?? '-'}</h3>
            <p>Total de Cálculos</p>
          </div>
        </StatCard>
      </StatsGrid>

      <Section>
        <SectionHeader>
          <h2>
            <FiBarChart2 />
            Métricas da API
          </h2>
          <PeriodButtonGroup>
            {METRICS_PERIODS.map((period) => (
              <ActionButton
                key={period.key}
                $variant={metricsPeriod === period.key ? 'primary' : undefined}
                onClick={() => setMetricsPeriod(period.key)}
                disabled={loadingMetrics}
              >
                {period.label}
              </ActionButton>
            ))}
            <ActionButton onClick={fetchMetrics} disabled={loadingMetrics}>
              <FiRefreshCw />
              Atualizar
            </ActionButton>
          </PeriodButtonGroup>
        </SectionHeader>

        {loadingMetrics ? (
          <LoadingState>
            <Spinner />
          </LoadingState>
        ) : metricsError === 'forbidden' ? (
          <EmptyState>
            <FiShield />
            <p>Acesso restrito</p>
          </EmptyState>
        ) : metricsError ? (
          <EmptyState>
            <FiAlertTriangle />
            <p>Erro ao carregar métricas</p>
          </EmptyState>
        ) : (
          <>
            <div style={{ padding: '24px 24px 0' }}>
              <StatsGrid>
                <StatCard $color="#6464ff">
                  <div className="icon">
                    <FiActivity />
                  </div>
                  <div className="info">
                    <h3>{metricsSummary.totalRequests}</h3>
                    <p>Total de Requisições</p>
                  </div>
                </StatCard>

                <StatCard $color="#ef4444">
                  <div className="icon">
                    <FiXCircle />
                  </div>
                  <div className="info">
                    <h3>{metricsSummary.totalErrors}</h3>
                    <p>Total de Erros</p>
                  </div>
                </StatCard>

                <StatCard $color="#f59e0b">
                  <div className="icon">
                    <FiClock />
                  </div>
                  <div className="info">
                    <h3>{formatMs(metricsSummary.avgDuration)}</h3>
                    <p>Duração Média</p>
                  </div>
                </StatCard>

                <StatCard $color="#8b5cf6">
                  <div className="icon">
                    <FiClock />
                  </div>
                  <div className="info">
                    <h3>{formatMs(metricsSummary.maxDuration)}</h3>
                    <p>Duração Máxima</p>
                  </div>
                </StatCard>

                <StatCard $color="#10b981">
                  <div className="icon">
                    <FiHardDrive />
                  </div>
                  <div className="info">
                    <h3>{formatBytes(latestProcessSample?.rssBytes)}</h3>
                    <p>Memória RSS</p>
                  </div>
                </StatCard>

                <StatCard $color="#0ea5e9">
                  <div className="icon">
                    <FiZap />
                  </div>
                  <div className="info">
                    <h3>{formatMs(latestProcessSample?.eventLoopLagMs)}</h3>
                    <p>Atraso do Event Loop</p>
                  </div>
                </StatCard>

                <StatCard $color="#f97316">
                  <div className="icon">
                    <FiCpu />
                  </div>
                  <div className="info">
                    <h3>{formatMs(latestProcessSample?.cpuUserMs)}</h3>
                    <p>CPU (usuário)</p>
                  </div>
                </StatCard>

                <StatCard $color="#14b8a6">
                  <div className="icon">
                    <FiActivity />
                  </div>
                  <div className="info">
                    <h3>{latestProcessSample?.activeRequests ?? '-'}</h3>
                    <p>Requisições Ativas</p>
                  </div>
                </StatCard>
              </StatsGrid>
            </div>

            {sortedMetricsRows.length === 0 ? (
              <EmptyState>
                <FiBarChart2 />
                <p>Nenhuma requisição registrada no período</p>
              </EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Método</th>
                    <th>Rota</th>
                    <th>Status</th>
                    <th>Chamadas</th>
                    <th>Erros</th>
                    <th>Duração Média</th>
                    <th>Duração Mín</th>
                    <th>Duração Máx</th>
                    <th>Duração Acumulada</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedMetricsRows.map((row) => (
                    <tr key={`${row.method}-${row.route}-${row.statusCode}`}>
                      <td>{row.method}</td>
                      <td>
                        <code>{row.route}</code>
                      </td>
                      <td>
                        <Badge
                          $variant={row.statusCode >= 500 ? 'danger' : row.statusCode >= 400 ? 'warning' : 'success'}
                        >
                          {row.statusCode}
                        </Badge>
                      </td>
                      <td>{row.requestCount}</td>
                      <td>{row.errorCount}</td>
                      <td>{formatMs(row.requestCount > 0 ? row.totalDurationMs / row.requestCount : 0)}</td>
                      <td>{formatMs(row.minDurationMs)}</td>
                      <td>{formatMs(row.maxDurationMs)}</td>
                      <td>{formatMs(row.totalDurationMs)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <h2>
            <FiClock />
            Ultimos calculos
          </h2>
          <ActionButton onClick={handleLoadMoreLatestLogs} disabled={loadingLatestLogs || latestLogsLimit >= 100}>
            {latestLogsLimit >= 100 ? 'Limite maximo (100)' : `Ver mais (${latestLogsLimit}/100)`}
          </ActionButton>
        </SectionHeader>

        {loadingLatestLogs ? (
          <LoadingState>
            <Spinner />
          </LoadingState>
        ) : latestLogsError === 'forbidden' ? (
          <EmptyState>
            <FiShield />
            <p>Acesso restrito</p>
          </EmptyState>
        ) : latestLogsError ? (
          <EmptyState>
            <FiXCircle />
            <p>Erro ao carregar ultimos calculos</p>
          </EmptyState>
        ) : latestLogs.length === 0 ? (
          <EmptyState>
            <FiFileText />
            <p>Nenhum calculo encontrado</p>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Rota</th>
                <th>Criado em</th>
              </tr>
            </thead>
            <tbody>
              {latestLogs.map((log, index) => (
                <tr key={log.id || `${log.email || 'unknown'}-${log.route || 'unknown'}-${log.createdAt}-${index}`}>
                  <td>{log.email || '-'}</td>
                  <td>
                    <code>{log.route || '-'}</code>
                  </td>
                  <td>{formatLocalDateTime(log.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <h2>
            <FiUsers />
            Usuários
          </h2>
          <SearchInput type="text" placeholder="Buscar por email..." value={searchTerm} onChange={handleSearch} />
        </SectionHeader>

        {loading ? (
          <LoadingState>
            <Spinner />
          </LoadingState>
        ) : users.length === 0 ? (
          <EmptyState>
            <FiUserX />
            <p>{searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}</p>
          </EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Plano</th>
                  <th>Cadastro</th>
                  <th>Expira em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <ClickableRow key={user.id} onClick={(e) => handleUserClick(user, e)}>
                    <td>{user.email}</td>
                    <td>{getPlanBadge(user)}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{formatDate(user.subscriptionExpiresAt)}</td>
                    <td>
                      {canGrantCourtesy(user) && (
                        <ActionButton
                          $variant="primary"
                          onClick={() => handleCourtesy(user.id, true)}
                          disabled={loadingCourtesy === user.id}
                        >
                          <FiGift />
                          {loadingCourtesy === user.id ? 'Aguarde...' : 'Dar Cortesia'}
                        </ActionButton>
                      )}
                      {canRevokeCourtesy(user) && (
                        <ActionButton
                          $variant="danger"
                          onClick={() => handleCourtesy(user.id, false)}
                          disabled={loadingCourtesy === user.id}
                        >
                          <FiXCircle />
                          {loadingCourtesy === user.id ? 'Aguarde...' : 'Remover'}
                        </ActionButton>
                      )}
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <Pagination>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  Anterior
                </button>
                <span>
                  Página {page} de {totalPages}
                </span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Próxima
                </button>
              </Pagination>
            )}
          </>
        )}
      </Section>

      {selectedUser && (
        <ModalOverlay onClick={closeModal}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>
                <FiFileText />
                Histórico de {selectedUser.email}
              </h3>
              <button onClick={closeModal}>
                <FiX />
              </button>
            </ModalHeader>
            <ModalBody>
              {loadingLogs ? (
                <LoadingState>
                  <Spinner />
                </LoadingState>
              ) : logs.length === 0 ? (
                <EmptyState>
                  <FiFileText />
                  <p>Nenhum cálculo realizado</p>
                </EmptyState>
              ) : (
                <>
                  <LogList>
                    {logs.map((log) => (
                      <LogItem key={log.id}>
                        <span className="route">{log.route}</span>
                        <span className="date">{formatDateTime(log.createdAt)}</span>
                      </LogItem>
                    ))}
                  </LogList>
                  {logsTotalPages > 1 && (
                    <Pagination style={{ borderTop: 'none', paddingTop: '16px' }}>
                      <button
                        onClick={() => fetchLogs(selectedUser.id, logsPage - 1)}
                        disabled={logsPage === 1 || loadingLogs}
                      >
                        Anterior
                      </button>
                      <span>
                        Página {logsPage} de {logsTotalPages}
                      </span>
                      <button
                        onClick={() => fetchLogs(selectedUser.id, logsPage + 1)}
                        disabled={logsPage === logsTotalPages || loadingLogs}
                      >
                        Próxima
                      </button>
                    </Pagination>
                  )}
                </>
              )}
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  )
}
